import type { DtcCodeEntity } from '@packages/domain/dtc-codes';

import { colors } from '@packages/core/styles';
import { Button, Icon, LoadingCircle, Toggle, Typography } from '@packages/ui/shared';
import { TableCell, TableRow } from '@web/components/table';
import { useCallback } from 'react';
import { createUseStyles } from 'react-jss';

/**
 * Visual variants for the primary DTC button, keyed by primary status.
 */
const PRIMARY_DTC_BUTTON_VARIANTS = {
    isPrimary: {
        text: 'Primary',
        variant: 'token-blue',
        icon: 'star-filled',
        iconColor: 'blueOne',
    },
    isNotPrimary: {
        text: 'Set',
        variant: 'token-gray',
        icon: 'star-unfilled',
        iconColor: 'textDarkSecondary',
    },
} as const;

/**
 * Props for the {@link DtcCodesTableRow} component.
 */
type DtcCodesRowProps = {
    /** Whether this row's DTC code operation is in flight. */
    isLoading: boolean;

    /** Whether a primary code change is currently in flight (disables primary button for all rows). */
    isPrimaryUpdating: boolean;

    /** Callback to delete this DTC code from the case. */
    onDelete: (code: DtcCodeEntity) => void;

    /** Callback to toggle freeze frame data for this DTC code. */
    onFreezeFrameChange: (code: DtcCodeEntity, hasFreezeFrameDataInSSM: boolean) => void;

    /** Callback to set this DTC code as the primary code. */
    onPrimaryChange: (code: DtcCodeEntity) => void;

    /** The DTC code entity for this row. */
    rowData: DtcCodeEntity;
};

/** Dynamic JSS props for the DTC codes table row. */
type DtcRowStyleProps = {
    /** Whether this DTC code is the primary code. */
    isPrimary: boolean;
};

/**
 * Renders a single row in the DTC codes table with delete, primary designation, and freeze frame toggle controls.
 *
 * @param props - the component props
 * @returns a table row for a DTC code
 */
export function DtcCodesTableRow({
    isLoading = false,
    isPrimaryUpdating = false,
    onDelete,
    onFreezeFrameChange,
    onPrimaryChange,
    rowData,
}: DtcCodesRowProps) {
    const { code, isPrimary, hasFreezeFrameDataInSSM } = rowData;
    const classes = useStyles({ isPrimary, isLoading });

    const markPrimaryButton = PRIMARY_DTC_BUTTON_VARIANTS[isPrimary ? 'isPrimary' : 'isNotPrimary'];

    // Handlers

    /** Deletes this DTC code row. */
    const handleDeleteButtonClick = useCallback(() => {
        onDelete(rowData);
    }, [onDelete, rowData]);

    /** Sets this DTC code as the primary code. */
    const handlePrimaryButtonClick = useCallback(() => {
        onPrimaryChange(rowData);
    }, [onPrimaryChange, rowData]);

    /** Toggles freeze frame data for this DTC code. */
    const handleFreezeFrameToggleChange = useCallback(
        (checked: boolean) => {
            onFreezeFrameChange(rowData, checked);
        },
        [onFreezeFrameChange, rowData]
    );

    return (
        <TableRow className={classes.row}>
            <TableCell className={classes.tableCell}>
                <div className="d-flex align-items-center flex-row gap-2">
                    <Button disabled={isLoading} onPress={handleDeleteButtonClick} size="small" variant="ghost-blue">
                        {isLoading ? <LoadingCircle size={20} /> : <Icon name="trash" size={20} />}
                    </Button>
                    <Typography variant="label">{code}</Typography>
                </div>
            </TableCell>
            <TableCell className={classes.tableCell}>
                <div className="d-flex align-items-center flex-row gap-2">
                    <Button
                        disabled={isLoading || isPrimaryUpdating}
                        iconLeft={<Icon color={markPrimaryButton.iconColor} name={markPrimaryButton.icon} size={14} />}
                        onPress={handlePrimaryButtonClick}
                        size="small"
                        variant={markPrimaryButton.variant}
                    >
                        {markPrimaryButton.text}
                    </Button>
                </div>
            </TableCell>
            <TableCell className={classes.tableCell}>
                <div className={classes.content}>
                    <Toggle
                        checked={Boolean(hasFreezeFrameDataInSSM)}
                        disabled={isLoading}
                        onChange={handleFreezeFrameToggleChange}
                    />
                </div>
            </TableCell>
        </TableRow>
    );
}

/**
 * Returns the row background color based on whether this DTC code is the primary code.
 *
 * @param props - the JSS dynamic props
 * @param props.isPrimary - whether this DTC code is the primary code
 * @returns the background color string
 */
function getRowBackground({ isPrimary }: DtcRowStyleProps): string {
    return isPrimary ? '#FAFBFF' : colors.white;
}

const useStyles = createUseStyles({
    row: {
        background: getRowBackground,
        transition: 'background-color 0.15s',
    },
    tableCell: {
        padding: '12px 16px',
    },
    content: {
        minHeight: 30,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
});
